import React, { useState, useEffect, useRef } from "react";
import { MapPin, X, Navigation, Search, Loader2 } from "lucide-react";
import ActivityCard from "../user/activityCard/activityCard";
import "./SearchProsPage.css";

const PROFESSION_LIST = [
  "Carrosserie","Detailling","Mécanique","Nettoyage automobile",
  "Pare-brise","Pneu","Plombier","Remorquage","Serrurier","Serrurier automobile",
];

/* ====== BASE API alignée sur tes autres écrans ====== */
const RAW = (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) || process.env.REACT_APP_API_URL || "";
const API_BASE = (RAW ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "") : "") || "http://localhost:5000";

/* -------- fetchWithTimeout -------- */
async function fetchWithTimeout(input, init = {}, ms = 15000) {
  const timeoutCtrl = new AbortController();
  const ext = init.signal;
  let onAbort;
  if (ext) {
    if (ext.aborted) timeoutCtrl.abort();
    else {
      onAbort = () => timeoutCtrl.abort();
      ext.addEventListener("abort", onAbort, { once: true });
    }
  }
  const t = setTimeout(() => timeoutCtrl.abort(), ms);
  try {
    const { signal, ...rest } = init || {};
    return await fetch(input, { ...rest, signal: timeoutCtrl.signal });
  } finally {
    clearTimeout(t);
    if (ext && onAbort) ext.removeEventListener("abort", onAbort);
  }
}

/* -------- géoloc non bloquante (timeout court) -------- */
const tryGeolocQuick = () =>
  new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return resolve({ status: "unsupported" });
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ status: "ok", lat: coords.latitude, lon: coords.longitude }),
      (err) => {
        if (err && err.code === 1) return resolve({ status: "denied" });        // PERMISSION_DENIED
        if (err && err.code === 2) return resolve({ status: "unavailable" });   // POSITION_UNAVAILABLE
        if (err && err.code === 3) return resolve({ status: "timeout" });       // TIMEOUT
        return resolve({ status: "error" });
      },
      { enableHighAccuracy: false, timeout: 2500, maximumAge: 60000 }
    );
  });

/* -------- ouvrir réglages app (Capacitor si dispo) -------- */
async function openAppSettings() {
  try {
    // Capacitor v5+ (global)
    if (window?.Capacitor?.App?.openSettings) {
      await window.Capacitor.App.openSettings();
      return;
    }
    // Capacitor v3/v4 (Plugins)
    if (window?.Capacitor?.Plugins?.App?.openSettings) {
      await window.Capacitor.Plugins.App.openSettings();
      return;
    }
    alert("Ouvre les réglages de l’application et active la localisation.");
  } catch {
    alert("Impossible d’ouvrir les réglages automatiquement. Ouvre les réglages de l’app et active la localisation.");
  }
}

function SearchProsPage({ isApp = false }) {
  const [profession, setProfession] = useState("");
  const [activity, setActivity] = useState("");
  const [location, setLocation] = useState("");
  const [usingLocation, setUsingLocation] = useState(false);
  const [isLocationLocked, setIsLocationLocked] = useState(false);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [city, setCity] = useState("");
  const [searchMode, setSearchMode] = useState("none");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Popup "activer la localisation"
  const [showLocSettings, setShowLocSettings] = useState(false);

  // Suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const suggestAbortRef = useRef(null);
  const activityInputRef = useRef(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  // Fetch suggestions (debounced)
  useEffect(() => {
    const q = activity.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setHighlightIndex(-1);
      return;
    }

    const t = setTimeout(async () => {
      try {
        if (suggestAbortRef.current) suggestAbortRef.current.abort();
        suggestAbortRef.current = new AbortController();

        const url = `${API_BASE}/api/annonces/suggest?q=${encodeURIComponent(q)}`;

        const res = await fetchWithTimeout(
          url,
          {
            signal: suggestAbortRef.current.signal,
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          },
          10000
        );
        const data = await res.json().catch(() => null);
        if (Array.isArray(data)) {
          setSuggestions(data.slice(0, 8));
          setShowSuggestions(true);
          setHighlightIndex(-1);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
          setHighlightIndex(-1);
        }
      } catch {
        setSuggestions([]);
        setShowSuggestions(false);
        setHighlightIndex(-1);
      }
    }, 150);

    return () => clearTimeout(t);
  }, [activity, token]);

  // Recherche principale
  const handleSearchClick = async (customSearchMode) => {
    if (!profession) {
      alert("Veuillez sélectionner une profession.");
      return;
    }

    setIsLoading(true);
    try {
      let queryLat = latitude;
      let queryLon = longitude;
      let resolvedCity = city;

      // Géoloc NON BLOQUANTE si pas d’adresse saisie
      if (!location.trim()) {
        try {
          const geo = await tryGeolocQuick();
          if (geo?.status === "ok") {
            queryLat = geo.lat;
            queryLon = geo.lon;
            setLatitude(queryLat);
            setLongitude(queryLon);

            // reverse geocode en arrière-plan (non bloquant)
            (async () => {
              try {
                const r = await fetchWithTimeout(
                  `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${queryLat}&lon=${queryLon}`,
                  {},
                  8000
                );
                const d = await r.json().catch(() => null);
                if (d?.address) {
                  const ville = d.address.city || d.address.town || d.address.village || "";
                  setCity(ville);
                  setLocation((prev) => prev || ville);
                }
              } catch {}
            })();
          } else if (geo?.status === "denied") {
            setShowLocSettings(true);
          }
        } catch {}
      }

      // Si pas de lat/lon mais un texte de localisation → géocoder
      if ((!queryLat || !queryLon) && location.trim()) {
        try {
          const response = await fetchWithTimeout(
            `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=1&q=${encodeURIComponent(location)}`,
            {},
            10000
          );
          const data = await response.json().catch(() => null);
          if (Array.isArray(data) && data.length > 0) {
            const { lat, lon, address } = data[0];
            queryLat = parseFloat(lat);
            queryLon = parseFloat(lon);
            setLatitude(queryLat);
            setLongitude(queryLon);
            if (address && (address.city || address.town || address.village)) {
              const ville = address.city || address.town || address.village;
              setCity(ville);
              resolvedCity = ville;
            }
          }
        } catch {}
      }

      const currentSearchMode = customSearchMode || searchMode;
      const queryParams = new URLSearchParams({
        profession,
        q: activity || "",
        city: resolvedCity || "",
        location: location?.trim() || "",
      });

      if ((currentSearchMode === "distance" || currentSearchMode === "firstAvailable") && queryLat && queryLon) {
        queryParams.append("lat", String(queryLat));
        queryParams.append("lon", String(queryLon));
      }
      if (currentSearchMode === "distance") queryParams.append("sort", "distance");
      else if (currentSearchMode === "firstAvailable") queryParams.append("sort", "firstAvailable");

      const url = `${API_BASE}/api/annonces/search?${queryParams.toString()}`;

      const response = await fetchWithTimeout(
        url,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
        60000
      );

      const data = await response.json().catch(() => null);

      if (response.ok && Array.isArray(data)) {
        setResults(data);
        if (data.length === 0) alert("Aucune annonce trouvée.");
      } else if (response.ok && data && Array.isArray(data.results)) {
        setResults(data.results);
        if (data.results.length === 0) alert("Aucune annonce trouvée.");
      } else {
        const msg = (data && (data.message || data.error)) || `HTTP ${response.status}`;
        alert(`Erreur : ${msg}`);
        setResults([]);
      }
    } catch (e) {
      console.error("Recherche échouée :", e);
      setResults([]);
      alert("Erreur réseau. Réessaie plus tard.");
    } finally {
      setIsLoading(false);
      setSearchPerformed(true);
    }
  };

  const handleActivityKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      if (highlightIndex >= 0) {
        e.preventDefault();
        const s = suggestions[highlightIndex];
        setActivity(s.label);
        setShowSuggestions(false);
        setHighlightIndex(-1);
      } else {
        handleSearchClick();
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSearchClick(); };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert("La géolocalisation n'est pas supportée par votre navigateur.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const { latitude, longitude } = coords;
        setLatitude(latitude);
        setLongitude(longitude);

        try {
          const response = await fetchWithTimeout(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`,
            {},
            10000
          );
          const data = await response.json().catch(() => null);
          if (data && data.address) {
            const ville = data.address.city || data.address.town || data.address.village || "";
            setCity(ville);
            setLocation(ville);
            setUsingLocation(true);
            setIsLocationLocked(true);
          }
        } catch {}
      },
      (err) => {
        if (err?.code === 1) setShowLocSettings(true);
        else alert("Impossible de récupérer la géolocalisation.");
      }
    );
  };

  const handleClearLocation = () => {
    setLocation("");
    setLatitude(null);
    setLongitude(null);
    setUsingLocation(false);
    setIsLocationLocked(false);
    setCity("");
  };

  const wrapperClass = `container-card${isApp ? " container-card--app" : ""}`;
  const searchBarClass = `search-bar${isApp ? " search-bar--app" : ""}`;
  const searchInputClass = `search-input${isApp ? " search-input--app" : ""}`;
  const activityAutocompleteClass = `activity-autocomplete${isApp ? " activity-autocomplete--app" : ""}`;
  const suggestionListClass = `suggestion-list${isApp ? " suggestion-list--app" : ""}`;
  const suggestionItemClass = (highlight) => `suggestion-item${highlight ? " highlight" : ""}${isApp ? " suggestion-item--app" : ""}`;
  const locationWrapperClass = `location-input-wrapper${isApp ? " location-input-wrapper--app" : ""}`;
  const locationInputClass = `location-input${isApp ? " location-input--app" : ""}`;
  const searchButtonClass = `search-button${isApp ? " search-button--app" : ""}`;
  const resultsListClass = `results-list${isApp ? " results-list--app" : ""}`;
  const resultCardClass = `result-card${isApp ? " result-card--app" : ""}`;
  const searchModeSelectClass = `search-mode-select${isApp ? " search-mode-select--app" : ""}`;

  return (
    <div className={`welcome-user-page${isApp ? " welcome-user-page--app" : ""}`}>
      <div className={wrapperClass}>
        <header className="search-hero">
          <h1 className="search-page-title">Trouvez un professionnel</h1>
          <p className="search-hero__subtitle">
            Recherche par métier, activité et lieu — comparez et prenez rendez-vous en quelques clics.
          </p>
        </header>

        <div className="search-panel">
          <div className={searchBarClass} role="search" aria-label="Recherche de professionnels">
            <div className="search-field search-field--profession">
              <label className="search-field__label" htmlFor="search-profession">Profession</label>
              <select
                id="search-profession"
                className={`${searchInputClass} search-select`}
                value={profession}
                onChange={(e) => setProfession(e.target.value)}
              >
                <option value="">Choisir une spécialité</option>
                {PROFESSION_LIST.map((prof) => (
                  <option key={prof} value={prof}>{prof}</option>
                ))}
              </select>
            </div>

            <div className={`${activityAutocompleteClass} search-field search-field--activity`}>
              <label className="search-field__label" htmlFor="search-activity">Activité ou établissement</label>
              <input
                id="search-activity"
                ref={activityInputRef}
                type="text"
                className={searchInputClass}
                placeholder="Ex. révision, detailing, pare-brise…"
                value={activity}
                onChange={(e) => {
                  setActivity(e.target.value);
                  if (!e.target.value.trim()) {
                    setSuggestions([]);
                    setShowSuggestions(false);
                    setHighlightIndex(-1);
                  }
                }}
                onKeyDown={handleActivityKeyDown}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
                aria-autocomplete="list"
                role="combobox"
                aria-expanded={showSuggestions}
                aria-controls="suggest-list"
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul id="suggest-list" className={suggestionListClass} role="listbox">
                  {suggestions.map((s, idx) => (
                    <li
                      key={`${s.type}-${s.value}-${idx}`}
                      className={suggestionItemClass(idx === highlightIndex)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setActivity(s.label); setShowSuggestions(false); setHighlightIndex(-1); }}
                      role="option"
                      aria-selected={idx === highlightIndex}
                    >
                      <span className="sugg-label">{s.label}</span>
                      <span className={`sugg-badge ${s.type === "garage" ? "garage" : "activity"}`}>
                        {s.type === "garage" ? "Garage" : "Activité"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={`${locationWrapperClass} search-field search-field--location`}>
              <label className="search-field__label" htmlFor="search-location">Lieu</label>
              <div className="location-input-inner">
                <MapPin className="location-input-inner__pin" size={18} strokeWidth={2} aria-hidden />
                <input
                  id="search-location"
                  type="text"
                  className={locationInputClass}
                  placeholder="Ville ou adresse"
                  value={location}
                  onChange={(e) => !isLocationLocked && setLocation(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isLocationLocked}
                  aria-label="Localisation (adresse ou ville)"
                />
                <button
                  type="button"
                  className="location-icon-btn location-icon-btn--gps"
                  onClick={handleUseMyLocation}
                  title="Utiliser ma position"
                  aria-label="Utiliser ma position"
                >
                  <Navigation size={18} strokeWidth={2} />
                </button>
                {usingLocation && (
                  <button
                    type="button"
                    className="location-icon-btn location-icon-btn--clear"
                    onClick={handleClearLocation}
                    title="Effacer la localisation"
                    aria-label="Effacer la localisation"
                  >
                    <X size={18} strokeWidth={2} />
                  </button>
                )}
              </div>
            </div>

            <div className="search-bar__submit-wrap">
              <button
                type="button"
                className={searchButtonClass}
                onClick={() => handleSearchClick()}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="search-button__spin" size={20} strokeWidth={2} aria-hidden />
                    Recherche…
                  </>
                ) : (
                  <>
                    <Search size={20} strokeWidth={2} aria-hidden />
                    Rechercher
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="search-mode-row">
            <span className="search-mode-row__label" id="search-sort-label">Trier par</span>
            <div className="search-mode-row__control">
              <select
                id="search-sort"
                className={searchModeSelectClass}
                value={searchMode}
                onChange={async (e) => {
                  const val = e.target.value;
                  setSearchMode(val);
                  if (searchPerformed) await handleSearchClick(val);
                }}
                aria-labelledby="search-sort-label"
              >
                <option value="none">Pertinence</option>
                <option value="distance">Plus proche</option>
                <option value="firstAvailable">Premier rendez-vous disponible</option>
              </select>
            </div>
            {(searchMode === "distance" || searchMode === "firstAvailable") && !latitude && (
              <p className="search-mode-hint" role="note">
                Indiquez un lieu ou activez la géolocalisation pour ce tri.
              </p>
            )}
          </div>
        </div>

        {searchPerformed && (
          <p className="results-count" aria-live="polite">
            <span className="results-count__badge">{results.length}</span>
            <span className="results-count__text">
              {results.length === 1 ? "annonce trouvée" : "annonces trouvées"}
            </span>
          </p>
        )}

        {results.length > 0 && (
          <div className={resultsListClass}>
            <h3 className="results-list__title">Résultats</h3>
            {results.map((annonce) => (
              <div key={annonce._id} className={resultCardClass}>
                <div className="result-card__header">
                  <div className="result-card__title">
                    <strong>{annonce.nomProfil ? annonce.nomProfil : annonce.nameCompany}</strong>
                    {annonce.city && <span className="result-card__city">{annonce.city}</span>}
                    {annonce.distanceKm != null && (
                      <span className="result-card__distance"> • {annonce.distanceKm} km</span>
                    )}
                  </div>
                </div>
                <ActivityCard proId={annonce._id} profilId={annonce.profilId || null} annonceId={annonce._id} />
              </div>
            ))}
          </div>
        )}

        {searchPerformed && results.length === 0 && (
          <div className="container_user container_user--empty">
            <p className="empty-results__title">Aucun résultat</p>
            <p className="empty-results__hint">Élargissez la recherche ou modifiez le lieu.</p>
          </div>
        )}

        {showLocSettings && (
          <div className="loc-modal-backdrop" onClick={() => setShowLocSettings(false)} role="presentation">
            <div
              className="loc-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="loc-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="loc-modal__close"
                onClick={() => setShowLocSettings(false)}
                aria-label="Fermer"
              >
                <X size={20} strokeWidth={2} />
              </button>
              <h3 id="loc-modal-title" className="loc-modal__title">Activer la localisation</h3>
              <p className="loc-modal__text">
                Pour trier par distance et afficher les professionnels les plus proches, autorisez la localisation
                dans les réglages de votre appareil.
              </p>
              <div className="loc-modal__actions">
                <button type="button" className="loc-modal__btn loc-modal__btn--secondary" onClick={() => setShowLocSettings(false)}>
                  Plus tard
                </button>
                <button type="button" className="loc-modal__btn loc-modal__btn--primary" onClick={async () => { await openAppSettings(); }}>
                  Ouvrir les réglages
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchProsPage;

